import React, { useEffect, useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  MaterialReactTable,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
} from 'material-react-table';

// Shared Material React Table shell: container/scroll wiring, expand-state plumbing, and the
// standard toolbar/pagination/sorting toggles. Domain logic (columns, row shaping, sort headers,
// grouping) stays with each caller — see ConsistEventHistoryTable.js for a grouped-table consumer.
export const MaterialDataTable = ({
  className = 'material-data-table',
  tableTestId,
  scrollContainerClassName,
  ariaLabel,
  columns,
  data = [],
  isLoading = false,
  enableExpanding = false,
  getRowCanExpand,
  getSubRows,
  getRowId,
  expandedRows,
  onExpandedChange,
  hideExpandColumn,
  onReachBottom,
  canLoadMore,
  tableContainerRef,
  fitViewport = false,
  fillViewport = false,
  enableRowVirtualization = false,
  rowVirtualizerOptions,
  muiTableBodyRowProps,
  muiTableBodyCellProps,
  muiTopToolbarProps,
  muiExpandButtonProps,
  enablePagination = false,
  enableSorting = false,
  enableColumnActions = false,
  enableColumnFilters = false,
  enableTopToolbar = false,
  enableBottomToolbar = false,
  enableColumnResizing = false,
  enableStickyHeader = true,
  enableHiding = false,
  showHideColumnsTestId,
  topToolbarActions,
  toolbarInternalActions,
  enableFullScreenToggle = false,
  fullScreenToggleTestId,
  layoutMode = 'grid',
  sorting,
  onSortingChange,
  manualSorting,
  columnVisibility,
  onColumnVisibilityChange,
  showBuiltInColumnVisibility = true,
}) => {
  const internalContainerRef = useRef(null);
  const containerRef = tableContainerRef || internalContainerRef;
  const pageRequested = useRef(false);

  // Page fitting requires an .analysisContainer ancestor; .page-content and .main-footer
  // constrain its available space when present. Modal tables retain their own sizing.
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!fitViewport || !container || container.closest('.modal')) return;
    const page = container.closest('.analysisContainer');
    if (!page) return;
    const footer = document.querySelector('.main-footer');
    const pageContent = page.closest('.page-content');
    let frame;
    const measure = () => {
      if (!container.getClientRects().length || !container.clientWidth) return;
      const footerBounds = footer?.getBoundingClientRect();
      const pageContentBottom = pageContent
        ? pageContent.getBoundingClientRect().top + pageContent.clientTop + pageContent.clientHeight
        : window.innerHeight;
      const bottom = Math.min(footerBounds?.height ? footerBounds.top : window.innerHeight, pageContentBottom, window.innerHeight);
      const containerBounds = container.getBoundingClientRect();
      // Reserve trailing messages and ancestor spacing, not just space below the table itself.
      const trailingSpace = Math.max(16, page.getBoundingClientRect().bottom - containerBounds.bottom + 1);
      const top = containerBounds.top + (pageContent?.scrollTop || 0);
      const height = Math.max(160, Math.floor(bottom - top - trailingSpace));
      container.style.setProperty('--analysis-table-height', `${height}px`);
    };
    const schedule = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(measure);
    };
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(schedule);
    for (let element = container; element; element = element.parentElement) {
      observer?.observe(element);
      if (element === page) break;
    }
    if (pageContent) observer?.observe(pageContent);
    if (footer) observer?.observe(footer);
    window.addEventListener('resize', schedule);
    measure();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', schedule);
      container.style.removeProperty('--analysis-table-height');
    };
  }, [fitViewport, containerRef]);

  useEffect(() => {
    pageRequested.current = false;
  }, [data, isLoading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!fillViewport || !container || !onReachBottom || isLoading || !canLoadMore) return;
    const fill = () => {
      if (!pageRequested.current && container.clientHeight > 0 && container.clientWidth > 0
        && container.scrollHeight <= container.clientHeight + 1) {
        pageRequested.current = true;
        onReachBottom();
      }
    };
    const frame = requestAnimationFrame(fill);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(fill);
    observer?.observe(container);
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [fillViewport, containerRef, data, isLoading, canLoadMore, onReachBottom]);

  const handleScroll = event => {
    if (!onReachBottom || isLoading || !canLoadMore) return;
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 1) {
      if (fillViewport && pageRequested.current) return;
      pageRequested.current = true;
      onReachBottom();
    }
  };

  // Skeletons only on the empty first load; subsequent loads keep rows visible under a top progress bar.
  const hasRows = Array.isArray(data) && data.length > 0;
  const state = {
    isLoading: isLoading && !hasRows,
    showProgressBars: isLoading && hasRows,
  };
  if (expandedRows) state.expanded = expandedRows;

  // Kept out of `state` so MRT stays in charge of visibility and the show/hide menu still works.
  const initialState = hideExpandColumn ? { columnVisibility: { 'mrt-row-expand': false } } : undefined;

  // An explicit undefined handler overrides TanStack's internal state updater, so only forward defined ones.
  const changeHandlers = {
    ...(onSortingChange ? { onSortingChange } : {}),
    ...(onColumnVisibilityChange ? { onColumnVisibilityChange } : {}),
    ...(onExpandedChange ? { onExpandedChange } : {}),
  };

  return (
    <div className={`${className}${fitViewport ? ' analysis-page-table' : ''}`} data-testid={tableTestId}>
      <MaterialReactTable
        columns={columns}
        data={data}
        enableExpanding={enableExpanding}
        enableExpandAll={false}
        getRowCanExpand={getRowCanExpand}
        getSubRows={getSubRows}
        getRowId={getRowId}
        enablePagination={enablePagination}
        enableRowVirtualization={enableRowVirtualization}
        rowVirtualizerOptions={rowVirtualizerOptions}
        enableColumnActions={enableColumnActions}
        enableColumnFilters={enableColumnFilters}
        enableSorting={enableSorting}
        enableSortingRemoval={false}
        manualSorting={manualSorting}
        enableTopToolbar={enableTopToolbar}
        muiTopToolbarProps={muiTopToolbarProps}
        enableBottomToolbar={enableBottomToolbar}
        enableColumnResizing={enableColumnResizing}
        enableStickyHeader={enableStickyHeader}
        enableHiding={enableHiding}
        enableGlobalFilter={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        displayColumnDefOptions={{ 'mrt-row-expand': { enableHiding: false } }}
        renderToolbarInternalActions={
          enableHiding || enableFullScreenToggle || toolbarInternalActions
            ? ({ table }) => (
              <div className="material-data-table__toolbar-internal-actions">
                {enableHiding && showBuiltInColumnVisibility && <ShowHideColumnsButton table={table} data-testid={showHideColumnsTestId} />}
                {enableFullScreenToggle && <ToggleFullScreenButton table={table} data-testid={fullScreenToggleTestId} />}
                {typeof toolbarInternalActions === 'function' ? toolbarInternalActions({ table }) : toolbarInternalActions}
              </div>
            )
            : undefined
        }
        renderTopToolbarCustomActions={topToolbarActions ? () => topToolbarActions : undefined}
        layoutMode={layoutMode}
        state={{ ...state, ...(sorting ? { sorting } : {}), ...(columnVisibility ? { columnVisibility } : {}) }}
        {...changeHandlers}
        initialState={initialState}
        muiTableProps={{ 'aria-label': ariaLabel }}
        muiTablePaperProps={{ elevation: 0, square: true }}
        muiTableContainerProps={{
          className: scrollContainerClassName,
          ref: containerRef,
          'data-testid': tableTestId ? `${tableTestId}-scroll` : undefined,
          onScroll: onReachBottom ? handleScroll : undefined,
        }}
        muiTableBodyRowProps={muiTableBodyRowProps}
        muiTableBodyCellProps={muiTableBodyCellProps}
        muiExpandButtonProps={muiExpandButtonProps}
      />
    </div>
  );
};

MaterialDataTable.propTypes = {
  className: PropTypes.string,
  tableTestId: PropTypes.string,
  scrollContainerClassName: PropTypes.string,
  ariaLabel: PropTypes.string,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array,
  isLoading: PropTypes.bool,
  enableExpanding: PropTypes.bool,
  getRowCanExpand: PropTypes.func,
  getSubRows: PropTypes.func,
  getRowId: PropTypes.func,
  expandedRows: PropTypes.object,
  onExpandedChange: PropTypes.func,
  hideExpandColumn: PropTypes.bool,
  onReachBottom: PropTypes.func,
  canLoadMore: PropTypes.bool,
  tableContainerRef: PropTypes.object,
  fitViewport: PropTypes.bool,
  fillViewport: PropTypes.bool,
  enableRowVirtualization: PropTypes.bool,
  rowVirtualizerOptions: PropTypes.object,
  muiTableBodyRowProps: PropTypes.func,
  muiTableBodyCellProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  muiTopToolbarProps: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  muiExpandButtonProps: PropTypes.func,
  enablePagination: PropTypes.bool,
  enableSorting: PropTypes.bool,
  enableColumnActions: PropTypes.bool,
  enableColumnFilters: PropTypes.bool,
  enableTopToolbar: PropTypes.bool,
  enableBottomToolbar: PropTypes.bool,
  enableColumnResizing: PropTypes.bool,
  enableStickyHeader: PropTypes.bool,
  enableHiding: PropTypes.bool,
  showHideColumnsTestId: PropTypes.string,
  topToolbarActions: PropTypes.node,
  toolbarInternalActions: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  enableFullScreenToggle: PropTypes.bool,
  fullScreenToggleTestId: PropTypes.string,
  layoutMode: PropTypes.string,
  sorting: PropTypes.array,
  onSortingChange: PropTypes.func,
  manualSorting: PropTypes.bool,
  columnVisibility: PropTypes.object,
  onColumnVisibilityChange: PropTypes.func,
  showBuiltInColumnVisibility: PropTypes.bool,
};
